import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Toaster, toast } from "sonner";
import { AlertCircle, Flag } from "lucide-react";
import ChatComposer from "@/components/chat/ChatComposer";
import MessageBubble from "@/components/chat/MessageBubble";
import FlightGroupCard from "@/components/cards/FlightGroupCard";
import HotelListCard from "@/components/cards/HotelListCard";
import PackageGroupCard from "@/components/cards/PackageGroupCard";
import WeatherCard from "@/components/cards/WeatherCard";
import ItineraryCard from "@/components/cards/ItineraryCard";
import MapCard from "@/components/cards/MapCard";
import EmptyResultCard from "@/components/cards/EmptyResultCard";
import ExperiencesGroupCard from "@/components/cards/ExperiencesGroupCard";
import CtaButtonsCard from "@/components/cards/CtaButtonsCard";
import LocalExperienceCard from "@/components/cards/LocalExperienceCard";
import PresenceAvatars from "@/components/PresenceAvatars";
import Logo from "@/components/Logo";
import AiDisclaimer from "@/components/AiDisclaimer";
import TypingDots from "@/components/TypingDots";
import ReportConversationDialog from "@/components/ReportConversationDialog";
import { useAuth, authHeaders } from "@/context/AuthContext";
import { useGeo } from "@/context/GeoContext";
import { useRealtimeRoom } from "@/lib/realtime";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function renderCard(card, key, onPlacesToSee) {
    if (card.type === "flight_group")
        return <FlightGroupCard key={key} card={card} onPlacesToSee={onPlacesToSee} />;
    if (card.type === "hotel_list")
        return <HotelListCard key={key} card={card} onPlacesToSee={onPlacesToSee} />;
    if (card.type === "package_group")
        return <PackageGroupCard key={key} card={card} onPlacesToSee={onPlacesToSee} />;
    if (card.type === "experiences_group")
        return <ExperiencesGroupCard key={key} card={card} />;
    if (card.type === "weather") return <WeatherCard key={key} card={card} />;
    if (card.type === "itinerary") return <ItineraryCard key={key} card={card} onSend={onPlacesToSee} />;
    if (card.type === "map") return <MapCard key={key} card={card} />;
    if (card.type === "empty_result")
        return <EmptyResultCard key={key} card={card} onPlacesToSee={onPlacesToSee} />;
    if (card.type === "cta_buttons")
        return <CtaButtonsCard key={key} card={card} onSend={onPlacesToSee} />;
    if (card.type === "local_experience")
        return <LocalExperienceCard key={key} card={card} />;
    return null;
}

export default function SharedSessionPage() {
    const { t } = useTranslation();
    const { token: shareToken } = useParams();
    const navigate = useNavigate();
    const { user, token: jwt } = useAuth();
    const { geo, userGeo, currencyPref } = useGeo();

    const [meta, setMeta] = useState(null);
    const [messages, setMessages] = useState([]);
    const [sending, setSending] = useState(false);
    const [loadError, setLoadError] = useState(null);
    const [joined, setJoined] = useState(false);
    const [reportOpen, setReportOpen] = useState(false);
    const scrollRef = useRef(null);

    // 1. Fetch share metadata (works anonymously).
    useEffect(() => {
        let cancelled = false;
        async function go() {
            try {
                const r = await axios.get(`${API}/share/${shareToken}`, {
                    headers: jwt ? authHeaders(jwt) : {},
                });
                if (!cancelled) setMeta(r.data);
            } catch (e) {
                if (!cancelled) setLoadError(e?.response?.status || "error");
            }
        }
        go();
        return () => {
            cancelled = true;
        };
    }, [shareToken, jwt]);

    // 2. Fetch the actual messages (share_token grants implicit read).
    useEffect(() => {
        if (!meta) return undefined;
        let cancelled = false;
        async function load() {
            try {
                const r = await axios.get(
                    `${API}/sessions/${meta.session_id}?share_token=${shareToken}`,
                    { headers: jwt ? authHeaders(jwt) : {} },
                );
                if (!cancelled) setMessages(r.data?.messages || []);
            } catch (e) {
                if (!cancelled) setLoadError(e?.response?.status || "error");
            }
        }
        load();
        return () => {
            cancelled = true;
        };
    }, [meta, shareToken, jwt]);

    // 3. Auto-scroll when new messages land.
    useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollTo({
                top: scrollRef.current.scrollHeight,
                behavior: "smooth",
            });
        }
    }, [messages, sending]);

    // 4. If permission is edit AND user is logged in AND not owner → lazy-join
    //    on first send. For live realtime even before the first send, we also
    //    proactively join so the owner sees the collaborator list update.
    useEffect(() => {
        if (!meta || !jwt || joined) return;
        if (meta.viewer_is_owner) return;
        if (meta.permission !== "edit") return;
        axios
            .post(`${API}/share/${shareToken}/join`, {}, { headers: authHeaders(jwt) })
            .then(() => setJoined(true))
            .catch(() => {
                /* ignore — 403 if owner flipped to view */
            });
    }, [meta, jwt, joined, shareToken]);

    // Realtime room: JWT wins if present; otherwise use the share_token.
    const rtToken = jwt || shareToken;
    const onServerEvent = useCallback((event, payload) => {
        setMessages((prev) => {
            if (prev.some((m) => m.id === payload.message_id)) return prev;
            if (event === "message:user") {
                return [
                    ...prev,
                    {
                        id: payload.message_id,
                        role: "user",
                        content: payload.text,
                        cards: [],
                        timestamp: payload.ts,
                        author_name: payload.author_name,
                    },
                ];
            }
            if (event === "message:assistant") {
                return [
                    ...prev,
                    {
                        id: payload.message_id,
                        role: "assistant",
                        content: payload.text,
                        cards: payload.cards || [],
                        timestamp: payload.ts,
                    },
                ];
            }
            return prev;
        });
    }, []);
    const onRevoke = useCallback(() => {
        toast(t("share.revoked_toast"));
        setTimeout(() => navigate("/"), 1500);
    }, [t, navigate]);

    const { presence, typing, emitTyping } = useRealtimeRoom({
        sessionId: meta?.session_id,
        token: rtToken,
        onServerEvent,
        onRevoke,
    });

    const canWrite = meta && user && (meta.viewer_is_owner || meta.permission === "edit");
    const needsSignup = meta && !user && meta.permission === "edit";

    const typingLabel = useMemo(() => {
        const others = typing.filter((x) => !user || x.user_id !== user.id);
        if (others.length === 0) return null;
        if (others.length === 1) return t("share.typing_one", { name: others[0].name });
        return t("share.typing_many", { count: others.length });
    }, [typing, user, t]);

    const assistantReplyTick = useMemo(
        () => messages.filter((m) => m.role === "assistant").length,
        [messages],
    );

    const handleSend = useCallback(
        async (raw) => {
            const text = (raw || "").trim();
            if (!text || sending || !canWrite) return;
            emitTyping(false);
            // Optimistic local append — WS echo guarded by id check.
            const localId = `local-${Date.now()}`;
            setMessages((prev) => [
                ...prev,
                {
                    id: localId,
                    role: "user",
                    content: text,
                    cards: [],
                    timestamp: new Date().toISOString(),
                },
            ]);
            setSending(true);
            try {
                if (!joined && meta && meta.permission === "edit" && !meta.viewer_is_owner) {
                    await axios.post(
                        `${API}/share/${shareToken}/join`,
                        {},
                        { headers: authHeaders(jwt) },
                    );
                    setJoined(true);
                }
                await axios.post(
                    `${API}/chat`,
                    {
                        session_id: meta.session_id,
                        message: text,
                        affiliate_region: geo?.affiliate_region || "WORLD",
                        currency_pref: currencyPref || geo?.currency || "USD",
                        user_geo: userGeo || null,
                    },
                    { headers: jwt ? authHeaders(jwt) : {} },
                );
                // Assistant turn arrives via WebSocket broadcast.
            } catch (e) {
                const status = e?.response?.status;
                if (status === 429) toast.error(t("share.rate_limited"));
                else toast.error("Could not send. Please try again.");
                setMessages((prev) => prev.filter((m) => m.id !== localId));
            } finally {
                setSending(false);
            }
        },
        [sending, canWrite, emitTyping, joined, meta, shareToken, jwt, geo, userGeo, currencyPref, t],
    );

    if (loadError === 404) {
        return (
            <div className="flex h-screen flex-col items-center justify-center gap-3 bg-white px-6 text-center">
                <AlertCircle size={28} className="text-[#DC2626]" />
                <h1 className="text-[20px] font-medium text-[#0A0A0A]">Link not found</h1>
                <p className="max-w-md text-[13.5px] text-[#737373]">
                    This share link has been revoked or never existed. Ask the owner for a fresh link.
                </p>
                <Link to="/" className="mt-3 text-[13px] text-[#0A0A0A] underline underline-offset-2">
                    Back to Piqpath
                </Link>
            </div>
        );
    }

    if (!meta) {
        return (
            <div className="flex h-screen items-center justify-center bg-white text-[13.5px] text-[#737373]">
                {t("common.loading")}
            </div>
        );
    }

    return (
        <div
            className="App flex h-screen flex-col bg-white text-[#0A0A0A]"
            data-testid="shared-page-root"
        >
            <Toaster
                position="top-center"
                toastOptions={{
                    style: {
                        background: "#0A0A0A",
                        color: "#FFFFFF",
                        border: "1px solid #0A0A0A",
                        fontFamily: "Inter, sans-serif",
                    },
                }}
            />

            <header className="sticky top-0 z-10 border-b border-[#E5E5E5] bg-white/90 backdrop-blur">
                <div className="mx-auto flex w-full max-w-[1000px] items-center justify-between gap-4 px-5 py-3">
                    <Link to="/" className="flex-shrink-0">
                        <Logo size="sm" />
                    </Link>
                    <div className="min-w-0 flex-1 text-center">
                        <p
                            className="truncate text-[13px] text-[#0A0A0A]"
                            data-testid="shared-banner"
                        >
                            {t("share.viewing_banner", { owner: meta.owner_name })}
                        </p>
                        {meta.title && (
                            <p className="truncate text-[11.5px] text-[#737373]">“{meta.title}”</p>
                        )}
                    </div>
                    <div className="flex items-center gap-2">
                        <span className="inline-flex items-center gap-1 text-[11px] font-medium text-[#16A34A]">
                            <span className="relative flex h-2 w-2">
                                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#16A34A] opacity-60" />
                                <span className="relative inline-flex h-2 w-2 rounded-full bg-[#16A34A]" />
                            </span>
                            {t("share.live_label")}
                        </span>
                        <PresenceAvatars members={presence} />
                        <button
                            type="button"
                            onClick={() => setReportOpen(true)}
                            data-testid="report-conversation-button"
                            aria-label="Report this conversation"
                            title="Report this conversation"
                            className="inline-flex h-7 w-7 items-center justify-center rounded-full text-[#9CA3AF] transition hover:bg-[#F5F5F5] hover:text-[#0A0A0A]"
                        >
                            <Flag size={13} strokeWidth={2} />
                        </button>
                    </div>
                </div>
            </header>
            <ReportConversationDialog
                open={reportOpen}
                onOpenChange={setReportOpen}
                shareToken={shareToken}
            />

            <main
                ref={scrollRef}
                className="scroll-area flex-1 overflow-y-auto"
                data-testid="shared-conversation-scroll"
            >
                <div className="mx-auto w-full max-w-[820px] px-5 py-10">
                    <div className="space-y-8">
                        {messages.map((m) => (
                            <div
                                key={m.id}
                                className="fade-up"
                                data-testid={`message-${m.role}`}
                            >
                                <MessageBubble role={m.role} content={m.content} author={m.author_name} />
                                {m.role === "assistant" && m.cards && m.cards.length > 0 && (
                                    <div className="mt-4 space-y-4">
                                        {m.cards.map((c, idx) =>
                                            renderCard(c, `${m.id}-card-${idx}`, handleSend),
                                        )}
                                    </div>
                                )}
                            </div>
                        ))}
                        {sending && (
                            <div className="fade-up" data-testid="assistant-thinking">
                                <MessageBubble role="assistant" content="" thinking />
                            </div>
                        )}
                        {typingLabel && (
                            <p
                                className="flex items-center gap-2 text-[12px] italic text-[#737373]"
                                data-testid="typing-indicator"
                            >
                                <span>✏️ {typingLabel}</span>
                                <TypingDots />
                            </p>
                        )}
                    </div>
                </div>
            </main>

            <footer className="sticky bottom-0 z-10 border-t border-[#E5E5E5] bg-white">
                <div className="mx-auto w-full max-w-[820px] px-5 py-4">
                    {canWrite ? (
                        <>
                            <ChatComposer
                                disabled={sending}
                                onSend={handleSend}
                                onType={emitTyping}
                                assistantReplyTick={assistantReplyTick}
                            />
                            <AiDisclaimer />
                        </>
                    ) : needsSignup ? (
                        <div
                            className="rounded-xl border border-[#0A0A0A] bg-[#F5F5F5] px-4 py-3 text-center text-[13.5px]"
                            data-testid="signup-cta"
                        >
                            <Link
                                to={`/signup?next=${encodeURIComponent(`/share/${shareToken}`)}`}
                                className="font-medium text-[#0A0A0A] underline underline-offset-2"
                            >
                                {t("share.signup_to_join")}
                            </Link>
                        </div>
                    ) : (
                        <div
                            className="rounded-xl border border-[#E5E5E5] bg-[#FAFAFA] px-4 py-3 text-center text-[12.5px] text-[#737373]"
                            data-testid="view-only-banner"
                        >
                            {t("share.view_only_composer", { owner: meta.owner_name })}
                        </div>
                    )}
                </div>
            </footer>
        </div>
    );
}
